/**
 * Episode descriptions and metadata.
 * Generated by scrape_episode_descriptions.py
 * Handles both single episodes and multiple episodes per guest.
 */

export interface EpisodeInfo {
  description: string;
  date?: string;
  url: string;
  slug: string;
}

export type EpisodeData = EpisodeInfo | EpisodeInfo[];

// This will be populated from the JSON file
let episodeDescriptionsCache: Record<string, EpisodeData> | null = null;

/**
 * Load episode descriptions from JSON file.
 * Returns a promise that resolves when data is loaded.
 */
export async function loadEpisodeDescriptions(): Promise<Record<string, EpisodeData>> {
  if (episodeDescriptionsCache) {
    return episodeDescriptionsCache;
  }

  try {
    // Try loading from public folder (Vite serves from /)
    const response = await fetch('/episodeDescriptions.json');
    if (!response.ok) {
      // Fallback: try loading as a module import
      console.warn('Could not load episodeDescriptions.json from public folder');
      return {};
    }
    episodeDescriptionsCache = await response.json();
    return episodeDescriptionsCache as Record<string, EpisodeData>;
  } catch (error) {
    console.warn('Error loading episode descriptions:', error);
    return {};
  }
}

/**
 * Get episode description(s) for a guest.
 * Returns the description string if single episode, or array of EpisodeInfo if multiple.
 */
export function getEpisodeDescription(guestName: string): string | EpisodeInfo[] | null {
  if (!episodeDescriptionsCache) {
    return null;
  }

  const data = episodeDescriptionsCache[guestName];
  if (!data) {
    return null;
  }

  // Single episode - return description string
  if (!Array.isArray(data)) {
    return data.description;
  }

  // Multiple episodes - return array
  return data;
}

/**
 * Get all episodes for a guest (handles both single and multiple).
 * Always returns an array for consistent handling.
 * Can optionally pass the loaded cache data.
 */
export function getGuestEpisodes(guestName: string, cache?: Record<string, EpisodeData>): EpisodeInfo[] {
  const dataSource = cache || episodeDescriptionsCache;
  if (!dataSource) {
    return [];
  }

  const data = dataSource[guestName];
  if (!data) {
    return [];
  }

  // Single episode - wrap in array
  if (!Array.isArray(data)) {
    return [data as EpisodeInfo];
  }

  // Multiple episodes
  return data;
}

/**
 * Get the primary episode description (first one if multiple).
 * Useful for displaying a one-liner beneath guest name.
 */
export function getPrimaryEpisodeDescription(guestName: string): string | null {
  const episodes = getGuestEpisodes(guestName);
  if (episodes.length === 0) {
    return null;
  }
  return episodes[0].description;
}

/**
 * Check if a guest has multiple episodes.
 * Can optionally pass the loaded cache data.
 */
export function hasMultipleEpisodes(guestName: string, cache?: Record<string, EpisodeData>): boolean {
  const dataSource = cache || episodeDescriptionsCache;
  if (!dataSource) {
    return false;
  }

  const data = dataSource[guestName];
  if (!data) {
    return false;
  }
  return Array.isArray(data) && data.length > 1;
}
